import { useLenis } from 'lenis/react'
import { useEffect, useRef } from 'react'
import { Eyebrow, PrismLight, Segments } from '@/components/ui'
import { showcase, type ShowcaseProject, type ShowcaseProjectId } from '@/content/site'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { DURATION, EASE, gsap, refreshScroll, ScrollTrigger, SplitText, useGSAP } from '@/lib/gsap'
import { clamp, damp } from '@/lib/math'
import { useAppStore } from '@/lib/store'
import { MiniSite } from './sites/MiniSite'
import styles from './Showcase.module.css'

type Mode = 'pinned' | 'grid' | 'carousel'
type Project = ShowcaseProject & { readonly id: ShowcaseProjectId }

const PROJECTS: readonly Project[] = showcase.projects
const TOTAL = String(PROJECTS.length).padStart(2, '0')
const pad = (value: number) => String(value).padStart(2, '0')

function paintProgress(
  counter: HTMLElement | null,
  rail: HTMLElement | null,
  index: number,
  progress: number,
): void {
  const text = pad(index + 1)
  if (counter && counter.textContent !== text) counter.textContent = text
  if (rail) rail.style.transform = `scaleX(${clamp(progress).toFixed(4)})`
}

function previewLabel(project: Project): string {
  return showcase.previewLabel
    .replace('{name}', project.name)
    .replace('{city}', project.city)
    .replace('{hook}', project.hook)
}

/**
 * Showcase (`#showcase`): eight concept mini-sites, each in its shop's own art direction,
 * presented as luminous glass-framed objects on white.
 *
 * - **Desktop:** a pinned horizontal gallery (ScrollTrigger pin + `containerAnimation` for
 *   per-card parallax and depth). Cards lean with Lenis velocity (≤3°, damped), tilt toward
 *   the cursor with a travelling light sheen, and the track can be dragged.
 * - **Phone:** the same vertical-scroll progression, composed as portrait phone frames.
 * - **Reduced motion:** no pin, skew or tilt; a calm grid (or the plain carousel on phones).
 */
export function Showcase() {
  const rootRef = useRef<HTMLElement>(null)
  const pinRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLOListElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const counterRef = useRef<HTMLSpanElement>(null)
  const railRef = useRef<HTMLSpanElement>(null)

  const reducedMotion = useReducedMotion()
  const isMobile = useMediaQuery(MEDIA.mobile)
  const finePointer = useMediaQuery(MEDIA.hover)
  const introStarted = useAppStore((s) => s.phase !== 'boot')
  const lenis = useLenis()
  const lenisRef = useRef(lenis)
  useEffect(() => {
    lenisRef.current = lenis
  }, [lenis])

  const mode: Mode = reducedMotion ? (isMobile ? 'carousel' : 'grid') : 'pinned'

  // Writes the "03 / 08" counter and rail straight to the DOM: no re-render per frame.
  const setProgress = (index: number, progress: number) =>
    paintProgress(counterRef.current, railRef.current, index, progress)

  // Header reveal: masked lines rising, then the eyebrow and lead.
  useGSAP(
    () => {
      const root = rootRef.current
      if (!introStarted || !root) return
      const reveal = root.querySelectorAll('[data-reveal]')
      if (reducedMotion) {
        gsap.from(reveal, {
          autoAlpha: 0,
          duration: 0.3,
          scrollTrigger: { trigger: root, start: 'top 80%', once: true },
        })
        return
      }
      const title = root.querySelector('h2')
      if (title) {
        SplitText.create(title, {
          type: 'lines',
          mask: 'lines',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 105,
              stagger: 0.08,
              duration: DURATION.cinematic,
              scrollTrigger: { trigger: title, start: 'top 82%', once: true },
            }),
        })
      }
      gsap.from(reveal, {
        y: 28,
        autoAlpha: 0,
        stagger: 0.08,
        duration: DURATION.cinematic,
        scrollTrigger: { trigger: root, start: 'top 75%', once: true },
      })
    },
    { scope: rootRef, dependencies: [introStarted, reducedMotion] },
  )

  // Desktop: the pinned, horizontally scrubbed gallery.
  useGSAP(
    () => {
      const pin = pinRef.current
      const track = trackRef.current
      const stage = stageRef.current
      if (mode !== 'pinned' || !pin || !track || !stage) return

      const slots = gsap.utils.toArray<HTMLElement>('[data-slot]', track)
      const distance = () => Math.max(0, track.scrollWidth - pin.clientWidth)

      // The counter follows the card nearest the middle of the screen, read from the scrubbed
      // tween (what you see), with positions cached on refresh: no layout reads per frame.
      let centers: number[] = []
      let travel = 0
      let middle = 0
      const measure = () => {
        centers = slots.map((slot) => slot.offsetLeft + slot.offsetWidth / 2)
        travel = distance()
        middle = pin.clientWidth / 2
      }
      const tween = gsap.to(track, {
        x: () => -distance(),
        ease: 'none',
        onUpdate: () => {
          const progress = tween.progress()
          const focus = travel * progress + middle
          let nearest = 0
          centers.forEach((center, index) => {
            if (Math.abs(center - focus) < Math.abs((centers[nearest] ?? 0) - focus))
              nearest = index
          })
          setProgress(nearest, progress)
        },
      })
      const trigger = ScrollTrigger.create({
        trigger: pin,
        start: 'top top',
        end: () => `+=${distance()}`,
        pin: true,
        scrub: 1,
        animation: tween,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onRefresh: measure,
      })
      measure()

      // Each card: rises from depth as it enters, and its layers drift at their own depths.
      for (const slot of slots) {
        const across = {
          trigger: slot,
          containerAnimation: tween,
          start: 'left right',
          end: 'right left',
          scrub: true,
        }
        const body = slot.querySelector('[data-body]')
        const plate = slot.querySelector('[data-plate]')
        const phone = slot.querySelector('[data-phone]')
        if (body) {
          gsap.fromTo(
            body,
            {
              scale: 0.86,
              rotationY: -18,
              autoAlpha: 0.25,
              transformPerspective: 1400,
              transformOrigin: '0% 50%',
            },
            {
              scale: 1,
              rotationY: 0,
              autoAlpha: 1,
              ease: EASE.out,
              scrollTrigger: { ...across, start: 'left 102%', end: 'left 48%' },
            },
          )
        }
        if (plate)
          gsap.fromTo(plate, { xPercent: 5 }, { xPercent: -5, ease: 'none', scrollTrigger: across })
        if (phone) gsap.fromTo(phone, { x: 44 }, { x: -44, ease: 'none', scrollTrigger: across })
        slot.querySelectorAll<HTMLElement>('[data-screen] [data-depth]').forEach((layer) => {
          const depth = Number(layer.dataset.depth) || 1
          gsap.fromTo(
            layer,
            { xPercent: 7 * depth },
            { xPercent: -7 * depth, ease: 'none', scrollTrigger: across },
          )
        })
      }

      // Opacity keeps cards focusable before entry; focus scrolls them into the pin.
      // Rise and fade only: scaling the whole gallery would re-rasterise every card per frame.
      gsap.fromTo(
        stage,
        { y: 140, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: pin, start: 'top bottom', end: 'top 35%', scrub: 1 },
        },
      )

      // Keyboard: focusing a card scrolls the page so it slides into the middle.
      const onFocus = (event: FocusEvent) => {
        const slot = (event.target as HTMLElement).closest<HTMLElement>('[data-slot]')
        if (!slot) return
        const offset = slot.offsetLeft + slot.offsetWidth / 2 - pin.clientWidth / 2
        const target = trigger.start + clamp(offset, 0, distance())
        const smooth = lenisRef.current
        if (smooth) smooth.scrollTo(target, { duration: 1.2 })
        else window.scrollTo({ top: target })
      }

      // Drag: pulling the track scrolls the page one-to-one; the scrub smooths it.
      let drag: { x: number; scroll: number; moved: boolean } | null = null
      const onDown = (event: PointerEvent) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return
        drag = { x: event.clientX, scroll: window.scrollY, moved: false }
      }
      const onMove = (event: PointerEvent) => {
        if (!drag) return
        const dx = event.clientX - drag.x
        if (!drag.moved && Math.abs(dx) < 6) return
        if (!drag.moved) {
          drag.moved = true
          track.setPointerCapture(event.pointerId)
          track.dataset.dragging = ''
        }
        const target = clamp(drag.scroll - dx * 1.2, trigger.start, trigger.end)
        const smooth = lenisRef.current
        if (smooth) smooth.scrollTo(target, { immediate: true })
        else window.scrollTo({ top: target })
      }
      const onUp = (event: PointerEvent) => {
        if (drag?.moved && track.hasPointerCapture(event.pointerId))
          track.releasePointerCapture(event.pointerId)
        delete track.dataset.dragging
        drag = null
      }

      track.addEventListener('focusin', onFocus)
      track.addEventListener('pointerdown', onDown)
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
      refreshScroll()

      return () => {
        track.removeEventListener('focusin', onFocus)
        track.removeEventListener('pointerdown', onDown)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        delete track.dataset.dragging
      }
    },
    { scope: rootRef, dependencies: [mode], revertOnUpdate: true },
  )

  // Desktop: velocity lean, cursor tilt and the light sheen, damped on one ticker.
  useGSAP(
    () => {
      const track = trackRef.current
      if (mode !== 'pinned' || !track) return

      const cards = gsap.utils.toArray<HTMLElement>('[data-slot]', track).map((slot) => {
        const skew = slot.querySelector('[data-skew]')
        const tilt = slot.querySelector('[data-tilt]')
        const sheen = slot.querySelector<HTMLElement>('[data-sheen]')
        const scene = slot.querySelector<HTMLElement>('[data-scene]')
        if (sheen) gsap.set(sheen, { xPercent: -50, yPercent: -50 })
        return {
          scene,
          setSkew: skew ? gsap.quickSetter(skew, 'skewX', 'deg') : null,
          setRX: tilt ? gsap.quickSetter(tilt, 'rotationX', 'deg') : null,
          setRY: tilt ? gsap.quickSetter(tilt, 'rotationY', 'deg') : null,
          setSX: sheen ? gsap.quickSetter(sheen, 'x', 'px') : null,
          setSY: sheen ? gsap.quickSetter(sheen, 'y', 'px') : null,
          setSO: sheen ? gsap.quickSetter(sheen, 'opacity') : null,
          rx: 0,
          ry: 0,
          sx: 0,
          sy: 0,
          so: 0,
          target: { rx: 0, ry: 0, sx: 0, sy: 0, so: 0 },
          active: false,
        }
      })

      const listeners = cards.map((card) => {
        const scene = card.scene
        if (!scene || !finePointer) return null
        const onMove = (event: PointerEvent) => {
          const rect = scene.getBoundingClientRect()
          const nx = clamp((event.clientX - rect.left) / rect.width)
          const ny = clamp((event.clientY - rect.top) / rect.height)
          card.target = {
            ry: (nx - 0.5) * 11,
            rx: (0.5 - ny) * 8,
            sx: nx * rect.width,
            sy: ny * rect.height,
            so: 1,
          }
          card.active = true
        }
        const onLeave = () => {
          card.target = { ...card.target, rx: 0, ry: 0, so: 0 }
        }
        scene.addEventListener('pointermove', onMove)
        scene.addEventListener('pointerleave', onLeave)
        return () => {
          scene.removeEventListener('pointermove', onMove)
          scene.removeEventListener('pointerleave', onLeave)
        }
      })

      let skew = 0
      const tick = (_time: number, deltaMs: number) => {
        const dt = Math.min(deltaMs / 1000, 0.05)
        const smooth = lenisRef.current
        const velocity = smooth?.isScrolling ? smooth.velocity : 0
        const next = damp(skew, clamp(-velocity * 0.09, -3, 3), 4.5, dt)
        const settle = Math.abs(next) < 0.002 && Math.abs(velocity) < 0.01
        if (next !== skew || settle) {
          skew = settle ? 0 : next
          for (const card of cards) card.setSkew?.(skew)
        }

        for (const card of cards) {
          if (!card.active) continue
          const t = card.target
          card.rx = damp(card.rx, t.rx, 5, dt)
          card.ry = damp(card.ry, t.ry, 5, dt)
          card.sx = card.so < 0.01 ? t.sx : damp(card.sx, t.sx, 7, dt)
          card.sy = card.so < 0.01 ? t.sy : damp(card.sy, t.sy, 7, dt)
          card.so = damp(card.so, t.so, 4, dt)
          card.setRX?.(card.rx)
          card.setRY?.(card.ry)
          card.setSX?.(card.sx)
          card.setSY?.(card.sy)
          card.setSO?.(card.so)
          const resting =
            t.so === 0 && Math.abs(card.rx) + Math.abs(card.ry) < 0.01 && card.so < 0.005
          if (resting) {
            card.active = false
            card.setRX?.(0)
            card.setRY?.(0)
            card.setSO?.(0)
          }
        }
      }
      gsap.ticker.add(tick)
      return () => {
        gsap.ticker.remove(tick)
        for (const off of listeners) off?.()
      }
    },
    { scope: rootRef, dependencies: [mode, finePointer], revertOnUpdate: true },
  )

  // Phone: a native swipe carousel with a soft coverflow and a live counter.
  useEffect(() => {
    const scroller = scrollerRef.current
    const track = trackRef.current
    const counter = counterRef.current
    const rail = railRef.current
    if (mode !== 'carousel' || !scroller || !track) return
    const slots = Array.from(track.querySelectorAll<HTMLElement>('[data-slot]'))
    const bodies = slots.map((slot) => slot.querySelector<HTMLElement>('[data-body]'))
    let centers: number[] = []
    let width = 1
    let frame = 0

    const measure = () => {
      centers = slots.map((slot) => slot.offsetLeft + slot.offsetWidth / 2)
      width = slots[0]?.offsetWidth || 1
    }
    const update = () => {
      frame = 0
      const middle = scroller.scrollLeft + scroller.clientWidth / 2
      let best = 0
      let bestDistance = Infinity
      centers.forEach((center, index) => {
        const offset = (center - middle) / width
        if (Math.abs(offset) < bestDistance) {
          bestDistance = Math.abs(offset)
          best = index
        }
        const body = bodies[index]
        if (!body || reducedMotion) return
        const k = Math.min(Math.abs(offset), 1.4)
        body.style.transform = `perspective(1000px) rotateY(${clamp(-offset * 16, -22, 22).toFixed(2)}deg) scale(${(1 - k * 0.08).toFixed(4)})`
        body.style.opacity = (1 - k * 0.3).toFixed(3)
      })
      const max = scroller.scrollWidth - scroller.clientWidth
      paintProgress(counter, rail, best, max > 0 ? scroller.scrollLeft / max : 0)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    const onResize = () => {
      measure()
      schedule()
    }
    const onFocus = (event: FocusEvent) => {
      const slot = (event.target as HTMLElement).closest<HTMLElement>('[data-slot]')
      if (!slot) return
      scroller.scrollTo({
        left: slot.offsetLeft + slot.offsetWidth / 2 - scroller.clientWidth / 2,
        behavior: reducedMotion ? 'instant' : 'smooth',
      })
    }

    measure()
    update()
    scroller.addEventListener('scroll', schedule, { passive: true })
    scroller.addEventListener('focusin', onFocus)
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(frame)
      scroller.removeEventListener('scroll', schedule)
      scroller.removeEventListener('focusin', onFocus)
      window.removeEventListener('resize', onResize)
      for (const body of bodies) {
        if (!body) continue
        body.style.transform = ''
        body.style.opacity = ''
      }
    }
  }, [mode, reducedMotion])

  const track = (
    <ol
      ref={trackRef}
      className={styles.track}
      aria-labelledby="showcase-title"
      data-cursor={mode === 'grid' ? undefined : 'drag'}
    >
      {PROJECTS.map((project, index) => (
        <ShowcaseCard key={project.id} project={project} index={index} phoneOnly={isMobile} />
      ))}
    </ol>
  )

  const progress = (
    <p className={styles.progress} aria-hidden="true">
      <span className="tabular">
        <span ref={counterRef}>01</span> / {TOTAL}
      </span>
      <span className={styles.rail}>
        <span ref={railRef} className={styles.railFill} />
      </span>
    </p>
  )

  return (
    <section
      ref={rootRef}
      id="showcase"
      aria-labelledby="showcase-title"
      className={cx(styles.root, styles[mode], isMobile && styles.carousel)}
      data-theme="light"
    >
      <header className={cx('container', styles.header)}>
        <Eyebrow data-reveal>{showcase.eyebrow}</Eyebrow>
        <h2 id="showcase-title" className={styles.title}>
          <Segments segments={showcase.title} />
        </h2>
        <p className={styles.lead} data-reveal>
          {showcase.lead}
        </p>
      </header>

      {mode === 'pinned' ? (
        <div ref={pinRef} className={styles.pin}>
          <PrismLight className={styles.light} placement="center" intensity={0.8} />
          <div ref={stageRef} className={styles.stage}>
            {track}
          </div>
          {progress}
        </div>
      ) : mode === 'carousel' ? (
        <div className={styles.gallery}>
          <PrismLight className={styles.light} placement="center" intensity={0.75} />
          <div ref={scrollerRef} className={styles.scroller} data-lenis-prevent-touch="">
            {track}
          </div>
          {progress}
        </div>
      ) : (
        <div className={styles.gallery}>
          <PrismLight className={styles.light} placement="top" intensity={0.6} />
          {track}
        </div>
      )}
    </section>
  )
}

interface ShowcaseCardProps {
  project: Project
  index: number
  /** Phones show the shop's mobile site alone, in a phone frame. */
  phoneOnly: boolean
}

/** One concept: a glass browser frame with the shop's site, its phone version and a caption. */
function ShowcaseCard({ project, index, phoneOnly }: ShowcaseCardProps) {
  const nameId = `showcase-${project.id}`
  const label = previewLabel(project)

  return (
    <li className={styles.slot} data-slot>
      <article
        className={styles.card}
        tabIndex={0}
        aria-labelledby={nameId}
        aria-describedby={`${nameId}-meta`}
      >
        <div className={styles.skew} data-skew>
          <div className={styles.body} data-body>
            <div className={styles.scene} data-scene>
              {phoneOnly ? null : <div className={styles.plate} data-plate aria-hidden="true" />}
              <div className={styles.tilt} data-tilt>
                {phoneOnly ? null : (
                  <div className={styles.browser}>
                    <div className={styles.chrome} aria-hidden="true">
                      <span className={styles.dots}>
                        <span />
                        <span />
                        <span />
                      </span>
                      <span className={styles.url}>{project.id}</span>
                    </div>
                    <div className={styles.screen} role="img" aria-label={label} data-screen>
                      <MiniSite project={project} layout="desktop" />
                    </div>
                    <div className={styles.sheen} aria-hidden="true">
                      <span data-sheen />
                    </div>
                  </div>
                )}
                <div className={styles.phone} data-phone aria-hidden={phoneOnly ? undefined : true}>
                  <div
                    className={styles.phoneScreen}
                    role={phoneOnly ? 'img' : undefined}
                    aria-label={phoneOnly ? label : undefined}
                  >
                    <MiniSite project={project} layout="phone" />
                  </div>
                  <span className={styles.island} />
                </div>
              </div>
            </div>

            <div className={styles.caption}>
              <span className={styles.index} aria-hidden="true">
                {pad(index + 1)}
              </span>
              <h3 id={nameId} className={styles.name}>
                {project.name}
              </h3>
              <span className={styles.concept}>{showcase.label}</span>
              <p id={`${nameId}-meta`} className={styles.meta}>
                {project.city}, {project.country} · {project.trade}
              </p>
            </div>
          </div>
        </div>
      </article>
    </li>
  )
}
